/**
 * Reklama atribusiyasi: foydalanuvchi qaysi reklamadan kelganini saqlaydi
 * va lid yuborilganda LMS hamda Meta Conversions API ga uzatadi.
 */
import { ALLOWED_SOURCES as STAFF_SOURCES } from "@/app/lead/constants";

const STORAGE_KEY = "ad_attribution";
// Meta fbc cookie 90 kun yashaydi; lidni reklamaga bog'lash uchun 28 kun yetarli.
const TTL_MS = 28 * 24 * 60 * 60 * 1000;

const URL_KEYS = [
  "fbclid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

type UrlKey = (typeof URL_KEYS)[number];

type StoredAttribution = Partial<Record<UrlKey, string>> & {
  landing_url?: string;
  fbclid_at?: number;
  captured_at: number;
};

export type LeadAttribution = Partial<Record<UrlKey, string>> & {
  fbc?: string;
  fbp?: string;
  client_user_agent?: string;
  event_source_url?: string;
  meta_event_id: string;
};

const isBrowser = () => typeof window !== "undefined";

const readCookie = (name: string): string | undefined => {
  if (!isBrowser()) return undefined;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : undefined;
};

const readStored = (): StoredAttribution | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as StoredAttribution;
    if (!data?.captured_at || Date.now() - data.captured_at > TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
};

/**
 * URL'da fbclid yoki utm_* bo'lsa, ularni saqlaydi (oxirgi bosilgan reklama yutadi).
 * Har bir sahifa ochilganda chaqiriladi.
 */
export const captureAdAttribution = () => {
  if (!isBrowser()) return;
  try {
    const params = new URLSearchParams(window.location.search);
    const found: Partial<Record<UrlKey, string>> = {};
    URL_KEYS.forEach((key) => {
      const value = params.get(key);
      if (value) found[key] = value.slice(0, 500);
    });
    if (!Object.keys(found).length) return;

    const now = Date.now();
    const next: StoredAttribution = {
      ...found,
      landing_url: window.location.href.slice(0, 1000),
      fbclid_at: found.fbclid ? now : undefined,
      captured_at: now,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // localStorage yopiq bo'lishi mumkin (private mode) — atribusiyasiz davom etamiz
  }
};

const generateEventId = (): string => {
  if (isBrowser() && window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

export const isStaffSource = (source?: string) =>
  !!source && STAFF_SOURCES.includes(source);

/**
 * Lid yuborilayotganda body'ga qo'shiladigan maydonlar.
 * fbc: avval Pixel qo'ygan _fbc cookie, bo'lmasa saqlangan fbclid'dan Meta formatida yig'iladi.
 */
export const getLeadAttribution = (): LeadAttribution => {
  const stored = isBrowser() ? readStored() : null;
  const fbcCookie = readCookie("_fbc");
  const fbc =
    fbcCookie ||
    (stored?.fbclid ? `fb.1.${stored.fbclid_at ?? stored.captured_at}.${stored.fbclid}` : undefined);

  const result: LeadAttribution = {
    meta_event_id: generateEventId(),
    fbc,
    fbp: readCookie("_fbp"),
    client_user_agent: isBrowser() ? navigator.userAgent : undefined,
    event_source_url: isBrowser() ? window.location.href.slice(0, 1000) : undefined,
  };

  URL_KEYS.forEach((key) => {
    if (stored?.[key]) result[key] = stored[key];
  });

  return result;
};

/**
 * GTM'dagi brauzer Pixel "Lead" tegi shu event_id ni ishlatishi kerak,
 * shunda Meta brauzer va server hodisasini bitta lid deb hisoblaydi (dedup).
 */
export const pushLeadEventToDataLayer = (metaEventId: string) => {
  if (!isBrowser()) return;
  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({ event: "lead_submitted", meta_event_id: metaEventId });
};
